<template>
  <SfModalPortal
    modal-id="external-company-selector-modal"
    data-walkthrough-id="external-company-selector-modal"
    data-testid="external-company-selector-modal"
    size="l"
    class="sf-border-box"
    :scrollable-content="true"
    :footer-padding="0"
    @opened="onModalOpened"
    @close="onCloseAndDismiss"
  >
    <template #title>
      <span v-if="!isLoading">
        {{ I18n.t('company_links.get_data') }}
      </span>
    </template>
    <template #body>
      <div v-if="isLoading" class="sf-pad-3 sf-level sf-level-center">
        <div class="sf-spinner"></div>
      </div>
      <div v-else>
        <span v-if="existingCompanyLinks && existingCompanyLinks.length">
          <div class="sf-row sf-below-1">
            <div class="sf-col">
              <div class="sf-level sf-level-between sf-below-0">
                <div class="sf-level-item">
                  <span class="sf-font-s sf-font-medium tw-text-sf-gray-dark">
                    {{ I18n.t('company_links.select_linked_company_title') }}
                  </span>
                </div>
                <div class="sf-level-item">
                  <SfButtonDs
                    variant="secondary"
                    size="sm"
                    icon-before="pencil"
                    @click="openManageCompanyLinksModal"
                  >
                    {{ I18n.t('company_links.manage_button') }}
                  </SfButtonDs>
                </div>
              </div>
            </div>
          </div>
          <div class="sf-row sf-below-3">
            <div class="sf-col">
              <span class="sf-font-s tw-text-sf-gray-darker">
                {{ selectCompanyMessage }}
              </span>
            </div>
          </div>
          <div class="sf-row">
            <SfInpageList class="sf-col" data-testid="linked-companies">
              <label
                v-for="(companyLink, index) in effectiveLinkedCompanies"
                :key="`company_${companyLink.sourceCompanyId}`"
                :for="`check_${companyLink.id}`"
                class="tw-m-0"
              >
                <SfInpageListItem
                  :key="companyLink.id"
                  :title="companyLink.sourceCompanyName"
                  :item-id="companyLink.id"
                  :data-selected="companyLink.isSelected"
                  :selected="companyLink.isSelected"
                  :class="
                    index === effectiveLinkedCompanies.length - 1
                      ? 'tw-border-b-0'
                      : ''
                  "
                  :data-testid="'c_' + companyLink.sourceCompanyId"
                  :data-walkthrough-id="`company-label-${companyLink.sourceCompanyId}`"
                  @item-click="
                    handleCompanySelection(companyLink, !companyLink.isSelected)
                  "
                >
                  <template #icon>
                    <span class="fa fa-link sf-font-m"></span>
                  </template>
                  <template #labels>
                    <span
                      v-if="ledgerLabel && !companyLink.inPeriod"
                      v-dompurify-html="
                        I18n.t('company_links.missing_external_period_error', {
                          period: ledgerLabel,
                        })
                      "
                      v-tooltip.html="
                        I18n.t(
                          'company_links.missing_external_period_error_tooltip',
                          {
                            company: companyLink.sourceCompanyName,
                          }
                        )
                      "
                      class="sf-label sf-label-danger sf-label-active"
                    ></span>
                  </template>
                  <template #subtitles>
                    <SfInpageListItemSubtitle v-if="companyLink.lastSyncedAt">
                      <template #icon>
                        <i class="fa fa-refresh"></i>
                      </template>
                      <template #default>
                        {{
                          I18n.t('company_links.last_synced_on', {
                            date: formatTimestamp(companyLink.lastSyncedAt, {
                              shouldUseLocalTimezone: true,
                              format: 'D MMM Y, HH:mm z',
                            }),
                          })
                        }}
                      </template>
                    </SfInpageListItemSubtitle>
                  </template>
                  <template #right>
                    <label :class="buttonClass">
                      <input
                        :id="`check_${companyLink.id}`"
                        :data-testid="`check_${companyLink.id}`"
                        :checked="companyLink.isSelected"
                        name="type"
                        :type="buttonType"
                        @click="
                          handleCompanySelection(
                            companyLink,
                            !companyLink.isSelected
                          )
                        "
                      />
                      <div class="sf-form-check-content">
                        <span
                          class="sf-level-item sf-form-check-indicator"
                        ></span>
                      </div>
                    </label>
                  </template>
                </SfInpageListItem>
              </label>
            </SfInpageList>
          </div>
        </span>
        <span v-else>
          <CompanyLinksEmptyState />
        </span>
      </div>
    </template>
    <template #footer>
      <div
        v-if="!isLoading && existingCompanyLinks && existingCompanyLinks.length"
        class="
          tw-px-6
          tw-py-4
          tw-w-full
          tw-border-solid
          tw-border
          tw-border-b-0
          tw-border-l-0
          tw-border-r-0
          tw-border-sf-neutral-lighter-1
        "
      >
        <div class="sf-level sf-level-between">
          <div class="sf-level sf-level-item sf-level-left">
            <SfButtonDs
              v-if="showClearAllButton"
              variant="secondary"
              @click="externalCompaniesStore.clearSelectedCompanies"
              >{{ I18n.t('account_selector_modal.clear_all') }}</SfButtonDs
            >
            <SfButtonDs
              v-if="hasDefaultChanged"
              variant="secondary"
              size="sm"
              @click="externalCompaniesStore.restoreDefaultCompanies"
              >{{
                I18n.t('account_selector_modal.restore_default')
              }}</SfButtonDs
            >
          </div>
          <div class="sf-level sf-level-item sf-level-right">
            <SfButtonDs variant="secondary" @click="onCloseAndDismiss">{{
              I18n.t('common.form.cancel')
            }}</SfButtonDs>
            <SfButtonDs variant="primary" @click="onSaveCompanySelection">{{
              I18n.t('common.save')
            }}</SfButtonDs>
          </div>
        </div>
      </div>
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import formatTimestamp from '@app/utils/time-formatter'
import type {
  TextPropertiesUpdateResponse,
  TextPropertiesUpdateParams,
  TextPropertiesDataToSubmit,
} from '../../../templates/types'
import type {
  ExistingCompanyLink,
  ExternalCompanySelectorModalParams,
} from '@app/application/company_links/types'

import SfInpageList from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list.vue'
import SfInpageListItem from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list-item.vue'
import SfInpageListItemSubtitle from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list-item-subtitle.vue'

import { storeToRefs } from 'pinia'
import { ref, computed, watch } from 'vue'

import api from '../../reconciliation/sections/account-entries-modal/api/index'
import { SfLog } from '@app/adapters/logger'
import CompanyLinksEmptyState from '@app/application/company_links/components/company-links-empty-state.vue'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import { SfModalPortal } from '@app/shared/vue/components/sf-modal'
import useI18n from '@app/shared/vue/composables/use-I18n'
import useElementExistence from '@app/shared/vue/composables/use-element-existence'

import { useCompanyLinksStore } from '@app/application/company_links/store/use-company-links'
import { useExternalCompaniesStore } from '@app/application/company_links/store/use-external-companies'
import { useUserStore } from '@app/shared/pinia/user'

defineOptions({
  name: 'ExternalCompanySelectorModal',
})

const I18n = useI18n()

interface Props {
  ledgerLabel: string
}

defineProps<Props>()

const currentUserId = window.Silverfin.currentUserId()

const isLoadingStore = ref(false)
const isLoadingSelection = ref(false)
const singleSelection = ref(true)

const updatePath = ref('')
const updateParams = ref<TextPropertiesUpdateParams>({})
const afterUpdate = ref<(response: TextPropertiesUpdateResponse) => void>(
  () => {}
)
const dataToSubmit = ref<TextPropertiesDataToSubmit>([])

// Check whether the modal selector is rendered through liquid
const isCompanyLinkingPresent = useElementExistence(
  '[data-behaviour="external-company-selector-modal"]'
)

const companyLinksStore = useCompanyLinksStore()
const externalCompaniesStore = useExternalCompaniesStore()
const { hasDefaultChanged, selectedCompanies, valueToSubmit } = storeToRefs(
  externalCompaniesStore
)
const userStore = useUserStore()

userStore.setUserId(parseInt(currentUserId, 10))

const { existingCompanyLinks, linkedCompaniesWithPeriod, initialized } =
  storeToRefs(companyLinksStore)

const loadCompanyLinksStore = async () => {
  if (!initialized.value) {
    isLoadingStore.value = true
    await companyLinksStore.initExistingCompanyLinks()
    isLoadingStore.value = false
  }
}

watch(
  isCompanyLinkingPresent,
  (newValue, oldValue) => {
    // Only load the links if the liquid trigger is present
    if (!oldValue && newValue) {
      loadCompanyLinksStore()
    }
  },
  { immediate: true }
)

const effectiveLinkedCompanies = computed(() =>
  existingCompanyLinks.value.map((companyLink) => ({
    ...companyLink,
    isSelected: selectedCompanies.value.some(
      (selectedCompany) => selectedCompany.id === companyLink.sourceCompanyId
    ),
    inPeriod: linkedCompaniesWithPeriod.value.includes(
      companyLink.sourceCompanyId
    ),
  }))
)

const openManageCompanyLinksModal = () => {
  window.modalCitizen.openModalPortal('manage-company-links-modal')
}

const isLoading = computed(() => {
  return isLoadingStore.value || isLoadingSelection.value || !initialized.value
})

const buttonClass = computed(() => {
  return {
    'sf-form-check sf-form': true,
    'sf-form-check-rounded': singleSelection.value,
  }
})

const buttonType = computed(() => {
  return singleSelection.value ? 'radio' : 'checkbox'
})

const showClearAllButton = computed(() => {
  return !singleSelection.value && selectedCompanies.value.length > 0
})

const selectCompanyMessage = computed(() => {
  return singleSelection.value
    ? I18n.t('company_links.select_linked_company_description')
    : I18n.t('company_links.select_linked_companies_description')
})

const onCloseAndDismiss = () => {
  externalCompaniesStore.clearSelectedCompanies()
  externalCompaniesStore.clearDefaultCompanies()
  window.modalCitizen.closeModalPortal()
}

const handleCompanySelection = (
  companyLink: ExistingCompanyLink,
  selected: boolean
) => {
  if (singleSelection.value) {
    externalCompaniesStore.clearSelectedCompanies()
    externalCompaniesStore.clearDefaultCompanies()
  }

  switch (selected) {
    case true:
      externalCompaniesStore.setSelectedCompany(companyLink)
      break
    case false:
      externalCompaniesStore.removeSelectedCompanyById(
        companyLink.sourceCompanyId
      )
      break
  }
}

const onSaveCompanySelection = async () => {
  try {
    isLoadingSelection.value = true

    const properties = {
      properties: [
        {
          ...updateParams.value,
          value: valueToSubmit.value,
        },
        ...(dataToSubmit.value ? dataToSubmit.value : []),
      ],
    }

    const response = await api.updateTextProperties({
      updatePath: updatePath.value,
      properties,
    })

    if (!response) {
      return
    }

    if (afterUpdate.value) {
      afterUpdate.value(response)
    }

    externalCompaniesStore.clearSelectedCompanies()
    isLoadingSelection.value = false
    window.modalCitizen.closeModalPortal()
  } catch (error) {
    SfLog.warn('Failed to update text properties', {
      extra: {
        error: error,
      },
    })
  }
}

const onModalOpened = async (
  modalParams?: ExternalCompanySelectorModalParams
) => {
  if (!modalParams) {
    return
  }

  externalCompaniesStore.clearSelectedCompanies()
  externalCompaniesStore.clearDefaultCompanies()

  isLoadingSelection.value = true
  singleSelection.value = modalParams.singleSelection ?? false

  updateParams.value = modalParams.updateParams
  updatePath.value = modalParams.updatePath
  afterUpdate.value = modalParams.afterUpdate
  dataToSubmit.value = modalParams.dataToSubmit

  if (modalParams.value) {
    await externalCompaniesStore.fetchSelectorExternalCompanies({
      value: modalParams.value,
      defaultValue: modalParams.defaultValue,
    })
  }
  isLoadingSelection.value = false
}
</script>
